import React from 'react';
import { useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { PageShell, BackHeader, Tab, TabsRow, Toast, Skeleton } from '../../components';
import { ftuxSignal } from '../../features/ftux/signals';
import { useAppSelector } from '@/store';
import { selectStudioPeriod } from '@/store/slices/studioPeriod';
import { useToast } from '../../hooks/useToast';
import { useAcquisitionChannelGate } from '../../hooks/useAcquisitionChannelGate';
import { useFeatureGate } from '../../hooks/useFeatureGate';
import { useShowInProgress } from '../../hooks/useShowInProgress';
import { ROUTE } from '../../routes';
import { consolePeriodFor, type ReportPeriod } from '../home/shared-period';
import {
  affiliateConsoleOverviewNext,
  fetchStatus,
  getAffiliateError,
  listCampaigns,
  listInvites,
  revokeInvite,
} from './api';
import { useAffiliateResource } from './hooks/useAffiliateResource';
import { useCampaignStats } from './hooks/useCampaignStats';
import { useAffiliatesRoster } from './hooks/useAffiliatesRoster';
import { useOpenConsole } from './hooks/useOpenConsole';
import { useRefetchOnVisible } from '../../hooks/useRefetchOnVisible';
import {
  AFFILIATE_MANAGEMENT_FLAG,
  AFFILIATE_TABS,
  isAffiliateTab,
  isPlanFilter,
  isCampaignFilter,
  type AffiliateTab,
  type CampaignFilter,
  type PlanFilter,
} from './constants';
import { OverviewKpis } from './components/OverviewKpis/OverviewKpis';
import { AffiliateSummaryCard } from './components/AffiliateSummaryCard/AffiliateSummaryCard';
import { AffiliatesTab } from './components/AffiliatesTab/AffiliatesTab';
import { CampaignsTab } from './components/CampaignsTab/CampaignsTab';
import { ReportsTab } from './components/ReportsTab/ReportsTab';
import { AffiliateForm } from './components/AffiliateForm/AffiliateForm';
import { CampaignForm } from './components/CampaignForm/CampaignForm';
import { InviteLinkSheet } from './components/InviteLinkSheet/InviteLinkSheet';
import { Notice } from './components/Notice/Notice';
import { LumioCampaigns } from './components/LumioCampaigns/LumioCampaigns';
import { normalizeBrandUrl, visibleLumioRows } from './lumio-import';
import { reusablePlans } from './mapping';
import { useLumioCampaigns } from './hooks/useLumioCampaigns';
import { useLumioImport } from './hooks/useLumioImport';
import { useVideoLibrary } from '@/nova/pages/creatives/useVideoLibrary';
import { useLandingDestinations } from '@/nova/pages/creatives/hooks/useLandingDestinations';
import { creativesServiceConfigured } from '@/nova/pages/creatives/creative-api';
import { versionKey } from '@/nova/pages/creatives/video-library-api';
import { videoCampaignExternalRef } from '@/nova/pages/creatives/tracking-link';
import { Stack, TabsBar } from './affiliates.styles';

export type AffiliatesProps = {
  styleText?: string;
  className?: string;
};

/**
 * Affiliates: the native management surface for the current brand's afilliates.bet
 * program. A this-month overview sits above three tabs (Affiliates · Campaigns ·
 * Reports); the affiliate drill-down manages the profile, status and site-IDs. All
 * data comes from `${userApiUrl}/affiliate/*` scoped to the displayed brand; the
 * secondary "Advanced Settings" button SSOs into the full external console.
 *
 * Tab, roster filter and both create/edit modals live in the URL query string so a
 * view is shareable and Back closes a modal.
 */
const Affiliates = ({ styleText = '', className = '' }: AffiliatesProps): React.ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast, show: showToast } = useToast();

  const displayBrand = useAppSelector((s) => s.casinoInstance.displayBrand);
  const casinoInstanceId = displayBrand?.id;
  // The brand's public domain, normalized to an absolute origin, for imported campaigns' landing URLs.
  const brandBaseUrl = normalizeBrandUrl(displayBrand?.casino_data?.details?.domain);

  // Cheap link-state probe (no provisioning). Only fetch the tab data once the feature
  // is enabled, so a disabled env doesn't fire a wall of AFFILIATES_NOT_ENABLED 500s.
  const status = useAffiliateResource(casinoInstanceId, fetchStatus);
  const enabled = status.data?.enabled === true;
  const dataCasinoInstanceId = enabled ? casinoInstanceId : undefined;

  const roster = useAffiliatesRoster(dataCasinoInstanceId);
  const campaigns = useAffiliateResource(dataCasinoInstanceId, listCampaigns);
  // Per-campaign Regs/FTDs activity for the campaign-card stat tiles (best-effort; matches by campaign id).
  const campaignStats = useCampaignStats(dataCasinoInstanceId, campaigns.data ?? []);
  // Org invites: the email-bound pending ones surface at the TOP of the roster (they need the owner's
  // attention), so they are fetched here at the page level rather than only inside the invite modal.
  const invites = useAffiliateResource(dataCasinoInstanceId, listInvites);
  const reloadInvites = invites.reload;
  const pendingInvites = React.useMemo(
    // Only email-bound pending invites represent a person to chase; link-only invites are open
    // shareable links, not "pending invitations", so they are not shown as roster rows.
    () => (invites.data ?? []).filter((invite) => (invite.status ?? 'pending_invite') === 'pending_invite' && Boolean(invite.email)),
    [invites.data],
  );
  const onRevokeInvite = React.useCallback(
    async (id: string): Promise<void> => {
      if (!dataCasinoInstanceId) return;
      try {
        await revokeInvite(dataCasinoInstanceId, id);
        showToast(t('Invite revoked'));
        reloadInvites();
      } catch (err: unknown) {
        showToast(getAffiliateError(err).message);
      }
    },
    [dataCasinoInstanceId, showToast, t, reloadInvites],
  );
  const openConsole = useOpenConsole(casinoInstanceId);

  // The full tabbed affiliate management (directory + create, campaigns, reports) is generally
  // available, its `affiliate-management` flag kept as a rollback switch. The minimal MVP (this
  // overview + the summary card with counts and the Manage Affiliates SSO) is always shown.
  const { isEnabled } = useFeatureGate();
  const showManagement = isEnabled(AFFILIATE_MANAGEMENT_FLAG);
  // The Campaigns tab's "Available Lumio campaigns" import list shows only under the plain "777" reveal
  // (`show_in_progress`), live on a flip, like the Lumio sections on the Acquisition page.
  const showLumioImport = useShowInProgress();

  // Acquisition (Lumio) campaigns available to import. Read scope is the brand tenant_id (Ad
  // Service), gated on `enabled` like the affiliate data. `|| undefined` normalizes an empty
  // tenant_id so we never fire an Ad Service call with a blank tenant; the hook then holds in
  // `loading` (as the sibling status/campaigns hooks do for a missing key). In practice an
  // affiliate-enabled brand always has a real tenant_id (affiliates is gated behind acquisition,
  // which requires one).
  const lumioTenantId = enabled ? displayBrand?.tenant_id || undefined : undefined;
  const lumio = useLumioCampaigns(lumioTenantId);
  // The brand's saved video ads, for campaign-card posters (a `creatives-video:` campaign shows its
  // video's key visual). Brand-scoped + best-effort, gated on `enabled` like the Lumio list. The
  // video library keys by NUMERIC tenant id (the creatives endpoints), so coerce the string tenant.
  const videoTenantId = lumioTenantId ? Number(lumioTenantId) || undefined : undefined;
  const videoLib = useVideoLibrary(videoTenantId);
  const lumioImport = useLumioImport({
    casinoInstanceId: dataCasinoInstanceId,
    brandBaseUrl,
    onApplied: campaigns.reload,
    onToast: showToast,
  });
  // Actionable Lumio rows: not-yet-imported ('new') or imported-but-drifted ('changed');
  // identical-imported ones are hidden. Recomputes as either list or the session set changes.
  // Only classify Lumio rows once the affiliate campaigns list has actually loaded. When the
  // campaigns fetch is loading or errored, `campaigns.data` is undefined (useAffiliateResource
  // drops data on error); treating that as an empty list would reclassify every already-imported
  // campaign as 'new' and offer a duplicate "Make available to Affiliates" create (createCampaign
  // with a duplicate external_ref, which has no delete API). Suppress the rows until the list is known.
  const lumioRows = React.useMemo(
    () => (campaigns.data ? visibleLumioRows(lumio.data ?? [], campaigns.data, lumioImport.appliedIds, brandBaseUrl) : []),
    [lumio.data, campaigns.data, lumioImport.appliedIds, brandBaseUrl],
  );

  // Ad Service key-art for the campaign cards, keyed by `campaign_id` (which an imported affiliate
  // campaign carries as its `external_ref`). Reuses `lumio.data` already fetched for the import rows,
  // so the thumbnails cost no extra request; campaigns with no art / no match fall back to a placeholder.
  const campaignArtByRef = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const ad of lumio.data ?? []) {
      if (ad.campaign_id && ad.master_key_art) map.set(ad.campaign_id, ad.master_key_art);
    }
    // Video-ad posters: a campaign minted from a video ad carries external_ref
    // `creatives-video:<versionKey>`; show that video's key visual (image_url, one poster per video
    // shared across its versions). Map every version's key to the poster so each such campaign matches.
    for (const video of videoLib.videos) {
      if (!video.image_url) continue;
      for (const version of video.video_versions) {
        if (version.created_at) {
          map.set(videoCampaignExternalRef(versionKey(version.created_at)), video.image_url);
        }
      }
    }
    return map;
  }, [lumio.data, videoLib.videos]);

  // --- URL-driven view state ------------------------------------------------
  const tab: AffiliateTab = isAffiliateTab(searchParams.get('tab')) ? (searchParams.get('tab') as AffiliateTab) : 'affiliates';
  const rawFilter = searchParams.get('filter');
  const filter: PlanFilter = isPlanFilter(rawFilter) ? rawFilter : 'all';
  // Campaigns status filter (its own `cf` param, independent of the roster `filter`). Defaults to
  // `active` so paused campaigns are hidden until the owner asks for them.
  const rawCampaignFilter = searchParams.get('cf');
  const campaignFilter: CampaignFilter = isCampaignFilter(rawCampaignFilter) ? rawCampaignFilter : 'active';

  const affiliateParam = searchParams.get('affiliate');
  const campaignParam = searchParams.get('campaign');
  // The invite-link sheet is a modal like the create/edit ones (its own `invitelink` param), so it
  // is shareable and closes on Back.
  const inviteOpen = searchParams.get('invitelink') !== null;

  // FTUX: the invite sheet opening ends the affiliates piece, in either mode (a no-op outside an FTUX step).
  const inviteSheetOpen = showManagement && enabled && Boolean(casinoInstanceId) && inviteOpen;
  React.useEffect(() => {
    if (inviteSheetOpen) ftuxSignal('affiliates-invite-opened');
  }, [inviteSheetOpen]);

  const openedHereRef = React.useRef(false);

  const setParams = React.useCallback(
    (mutate: (params: URLSearchParams) => void, replace: boolean): void => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace },
      );
    },
    [setSearchParams],
  );

  const onTabClick = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const id = event.currentTarget.dataset.id;
      setParams((p) => {
        if (id && id !== 'affiliates') p.set('tab', id);
        else p.delete('tab');
        p.delete('filter');
        p.delete('cf');
      }, false);
    },
    [setParams],
  );

  const onFilterSelect = React.useCallback(
    (value: PlanFilter) => {
      setParams((p) => {
        // 'all' is the neutral state, i.e. the absence of the param (not a stored value).
        if (value === 'all') p.delete('filter');
        else p.set('filter', value);
      }, false);
    },
    [setParams],
  );

  const onCampaignFilterSelect = React.useCallback(
    (value: CampaignFilter) => {
      setParams((p) => {
        // `active` is the default view, so it is the absence of the param, not a stored value.
        if (value === 'paused' || value === 'all') p.set('cf', value);
        else p.delete('cf');
      }, false);
    },
    [setParams],
  );

  const openModal = React.useCallback(
    (key: 'affiliate' | 'campaign' | 'invitelink', value: string) => {
      openedHereRef.current = true;
      setParams((p) => p.set(key, value), false);
    },
    [setParams],
  );

  const closeModal = React.useCallback(() => {
    if (openedHereRef.current) {
      openedHereRef.current = false;
      navigate(-1);
    } else {
      setParams((p) => {
        p.delete('affiliate');
        p.delete('campaign');
        p.delete('invitelink');
      }, true);
    }
  }, [navigate, setParams]);

  // The console opens on the range picked last: on this page's Reports tab while the owner stays here (it may be one
  // Home cannot show, such as a single past day, which is not shared), else the shared range, else this month.
  const studioPeriod = useAppSelector(selectStudioPeriod);
  const [reportsPick, setReportsPick] = React.useState<ReportPeriod | null>(null);
  const onOpenConsole = React.useCallback(() => {
    openConsole
      .open(affiliateConsoleOverviewNext(reportsPick ?? consolePeriodFor(studioPeriod)))
      .catch((err: unknown) => showToast(getAffiliateError(err).message));
  }, [openConsole, showToast, studioPeriod, reportsPick]);

  // Closing the affiliate edit sheet reloads the roster (a cheap re-read; the roster's own latest-wins guard drops it
  // if the brand changed). A commission plan assigned or removed inside it also reloads it through the form's onSaved
  // once the platform has the change, so a sheet closed mid-request still ends with the new plan on the card.
  const onAffiliateFormClose = React.useCallback(() => {
    closeModal();
    roster.reload();
  }, [closeModal, roster]);

  // Resolve the record a modal edits from the already-loaded lists.
  const editingAffiliate =
    affiliateParam && affiliateParam !== 'new' ? roster.raw.affiliates.find((a) => a.id === affiliateParam) : undefined;
  const affiliateModalOpen = affiliateParam === 'new' || editingAffiliate !== undefined;
  // Plans already in use across the roster, so an affiliate can get the same deal without a duplicate plan.
  const rosterPlans = React.useMemo(() => reusablePlans(roster.raw.structures), [roster.raw.structures]);
  const editingCampaign =
    campaignParam && campaignParam !== 'new' ? (campaigns.data ?? []).find((c) => c.id === campaignParam) : undefined;
  const campaignModalOpen = campaignParam === 'new' || editingCampaign !== undefined;

  // The brand's live landing pages, for the campaign form's destination picker and the campaign cards' art. Read
  // only on the Campaigns tab or while the campaign form is open (the hook keeps it per brand for a couple of
  // minutes). Without spark-creatives the picker offers the homepage and a custom URL only.
  const creativesTenantId = creativesServiceConfigured() ? videoTenantId : undefined;
  const landing = useLandingDestinations(
    showManagement && (tab === 'campaigns' || campaignModalOpen) ? creativesTenantId : undefined,
  );

  // The campaigns list loads once and never polls, so a campaign created in the external
  // console (a separate tab/window) stays invisible here until a reload. Refetch it whenever
  // the owner returns to this tab, so a just-created campaign shows without a manual refresh.
  // Suppressed while a create/edit modal is open: a transient refetch failure clears the list
  // (`useAffiliateResource` drops data on error), which would unmount the open form and lose
  // any in-progress edits.
  useRefetchOnVisible(campaigns.reload, enabled && !affiliateModalOpen && !campaignModalOpen);

  // --- gate ------------------------------------------------------------------
  const emailNotVerified = roster.errorCode === 'AFFILIATES_EMAIL_NOT_VERIFIED';

  // Literal-keyed tab labels so the i18n extractor harvests each (a dynamic t() would not).
  const tabLabels: Record<AffiliateTab, string> = {
    affiliates: t('Affiliates'),
    campaigns: t('Campaigns'),
    reports: t('Reports'),
  };

  // The console entrypoint moved into the AffiliateSummaryCard ("Manage Affiliates"), so the
  // header carries no action.
  const header = <BackHeader title={t('Affiliates')} />;

  let body: React.ReactNode;
  if (!casinoInstanceId || status.loading) {
    body = <Skeleton height={280} block />;
  } else if (status.error) {
    body = <Notice icon="warning" title={t('Could not load affiliates')} body={status.error} actionLabel={t('Retry')} onAction={status.reload} />;
  } else if (!enabled) {
    body = (
      <Notice
        icon="affiliates"
        title={t('Affiliate program not available yet')}
        body={t('The affiliate program is not set up for this brand. Contact support to enable it.')}
      />
    );
  } else if (emailNotVerified) {
    body = (
      <Notice
        icon="mail"
        title={t('Verify your email to continue')}
        body={t('Your account email must be verified before you can manage affiliates. Verify it in Settings, then reload.')}
        actionLabel={t('Reload')}
        onAction={roster.reload}
      />
    );
  } else if (roster.error) {
    // The roster is the page's primary data (its doc calls a failure here the page-level error). A
    // failed list read must not fall through to the tabs and paint "No affiliates yet" (a false empty
    // that tempts re-creating records that exist) - surface it with a Retry, like status.error above.
    body = (
      <Notice icon="warning" title={t('Could not load affiliates')} body={roster.error} actionLabel={t('Retry')} onAction={roster.reload} />
    );
  } else {
    body = (
      <Stack>
        <OverviewKpis
          metrics={roster.overview}
          loading={roster.loading}
          aside={
            <AffiliateSummaryCard
              affiliateCount={roster.raw.affiliates.length}
              campaignCount={campaigns.data?.length}
              loading={roster.loading || campaigns.loading}
              onManage={onOpenConsole}
            />
          }
        />

        {showManagement ? (
          <>
            <TabsBar>
              <TabsRow>
                {AFFILIATE_TABS.map((entry) => (
                  <Tab
                    key={entry.id}
                    id={entry.id}
                    label={tabLabels[entry.id]}
                    icon={entry.icon}
                    active={tab === entry.id}
                    onClick={onTabClick}
                  />
                ))}
              </TabsRow>
            </TabsBar>

            {tab === 'affiliates' ? (
              <AffiliatesTab
                roster={roster.roster}
                loading={roster.loading}
                filter={filter}
                onFilterSelect={onFilterSelect}
                pendingInvites={pendingInvites}
                invitesError={invites.error}
                onRetryInvites={reloadInvites}
                onRevokeInvite={onRevokeInvite}
                onInvite={() => openModal('invitelink', '1')}
                onOpen={(id) => openModal('affiliate', id)}
              />
            ) : null}
            {tab === 'campaigns' ? (
              <>
                <CampaignsTab
                  campaigns={campaigns.data ?? []}
                  loading={campaigns.loading}
                  filter={campaignFilter}
                  onFilterSelect={onCampaignFilterSelect}
                  artByRef={campaignArtByRef}
                  artByUrl={landing.destinations}
                  error={campaigns.error}
                  onRetry={campaigns.reload}
                  statsById={campaignStats.byId}
                  statsLoaded={campaignStats.loaded}
                  onCreate={() => openModal('campaign', 'new')}
                  onOpen={(id) => openModal('campaign', id)}
                />
                {showLumioImport ? (
                  <LumioCampaigns
                    rows={lumioRows}
                    loading={lumio.loading}
                    error={lumio.error}
                    onRetry={lumio.reload}
                    importingIds={lumioImport.importingIds}
                    onApply={lumioImport.apply}
                  />
                ) : null}
              </>
            ) : null}
            {tab === 'reports' ? (
              <ReportsTab casinoInstanceId={casinoInstanceId} openingPeriod={reportsPick} onPeriodPick={setReportsPick} />
            ) : null}
          </>
        ) : null}
      </Stack>
    );
  }

  return (
    <PageShell styleText={styleText} className={className}>
      {header}
      {body}

      {showManagement && enabled && casinoInstanceId && affiliateModalOpen ? (
        <AffiliateForm
          key={affiliateParam ?? 'new'}
          casinoInstanceId={casinoInstanceId}
          affiliate={editingAffiliate}
          reusablePlans={rosterPlans}
          onClose={onAffiliateFormClose}
          onSaved={roster.reload}
          onToast={showToast}
        />
      ) : null}
      {showManagement && enabled && casinoInstanceId && campaignModalOpen ? (
        <CampaignForm
          key={campaignParam ?? 'new'}
          casinoInstanceId={casinoInstanceId}
          campaign={editingCampaign}
          brandBaseUrl={brandBaseUrl}
          landingDestinations={landing.destinations}
          landingLoading={landing.loading}
          onClose={closeModal}
          onSaved={campaigns.reload}
          onToast={showToast}
        />
      ) : null}
      {showManagement && enabled && casinoInstanceId && inviteOpen ? (
        <InviteLinkSheet casinoInstanceId={casinoInstanceId} onClose={closeModal} onCreated={reloadInvites} onToast={showToast} />
      ) : null}

      <Toast toast={toast} />
    </PageShell>
  );
};

/**
 * Route entry. Affiliates ships behind the acquisition self-managed feature flag: while
 * it is locked its Acquisition tile is inert, so guard direct-URL access too by bouncing
 * back to Acquisition. Reactive, so a runtime `show_in_progress` flip reveals it with no reload.
 */
const AffiliatesRoute = (props: AffiliatesProps): React.ReactElement => {
  const { isLocked } = useAcquisitionChannelGate();
  if (isLocked('affiliates')) return <Navigate to={ROUTE.acquisition()} replace />;
  return <Affiliates {...props} />;
};

export default AffiliatesRoute;
